"use client";
import { useEffect, useMemo, useState } from "react";
import { filter as lodashFilter, orderBy } from "lodash";
import contactService, { FormRequest } from "@/app/_services/contactService";
import { STATUS_LABELS } from "./_cmp/requestLabels";
import { toast } from "@/app/_utils/toast";

export type StatusFilter = FormRequest["status"] | "all";
export type FormTypeFilter = FormRequest["form_type"] | "all";

export function useRequestsModel() {
  const [requests, setRequests] = useState<FormRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [formTypeFilter, setFormTypeFilter] = useState<FormTypeFilter>("all");
  const [selectedRequest, setSelectedRequest] = useState<FormRequest | null>(null);

  async function loadRequests() {
    setIsLoading(true);
    try {
      const res = await contactService.getContactUs();
      setRequests(orderBy(res, ["id"], ["desc"]));
    } catch (e) {
      toast.error("Failed to load requests");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadRequests();
  }, []);

  const filteredRequests = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lodashFilter(requests, (r) => {
      if (q && !r.name?.toLowerCase().includes(q)) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (formTypeFilter !== "all" && r.form_type !== formTypeFilter) return false;
      return true;
    });
  }, [requests, search, statusFilter, formTypeFilter]);

  async function handleUpdateStatus(request: FormRequest, status: FormRequest["status"]) {
    const previous = request.status;
    setRequests((prev) => prev.map((r) => (r.id === request.id ? { ...r, status } : r)));
    try {
      await contactService.updateStatus(request.id, status);
      toast.success(`Marked as ${STATUS_LABELS[status]}`);
    } catch (e) {
      setRequests((prev) => prev.map((r) => (r.id === request.id ? { ...r, status: previous } : r)));
      toast.error("Failed to update request status");
    }
  }

  return {
    filteredRequests,
    isLoading,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    formTypeFilter,
    setFormTypeFilter,
    selectedRequest,
    openRequest: setSelectedRequest,
    closeRequest: () => setSelectedRequest(null),
    handleUpdateStatus,
  };
}
